/**
 * La marca, construida según el manual v1.0 (septiembre 2026).
 *
 * LA MARCA ES UNA PLACA: la pieza metálica que identifica equipos, pallets y
 * guías de despacho en una faena. Dentro va una flecha de norte cartográfico
 * apoyada en una base de cobre — orientación y origen del mineral en un gesto.
 *
 * La construcción NO es aproximada, viene del manual y por eso está en una
 * grilla y no en píxeles sueltos: módulo `x` = 1/6 del lado de la placa; la
 * flecha ocupa 4x de ancho y se apoya a 1x del borde inferior; la base de cobre
 * mide 4x × 0,5x y nunca cambia de proporción. Dibujarlo con un `viewBox` de
 * 6x6 módulos hace que esas reglas sean el código, no un comentario que se
 * puede desincronizar del trazo.
 *
 * Las cuatro versiones del manual, con su uso:
 *   A · vertical    — principal; pantallas de acceso, app icon, redes.
 *   B · horizontal  — barra de navegación, membretes, firmas.  ← web y app
 *   C · solo símbolo— favicon, sello de despacho, avatar.
 *   D · monocromo   — troquel, grabado, una tinta.
 *
 * Vive aparte porque lo usan el `Layout` y las pantallas de acceso, y en la v1
 * estaba copiado en cada una con medidas que se fueron separando entre sí.
 */

/** Módulo de la grilla: 1/6 del lado de la placa. Todo se mide en múltiplos. */
const X = 10
const LADO = X * 6

/**
 * Bajo este tamaño la base de cobre se elimina: el manual lo pide porque a esa
 * escala se empasta con la flecha y ensucia el símbolo en vez de firmarlo.
 */
const MINIMO_CON_BASE = 24

/**
 * La flecha de norte, 4x de ancho y centrada en el eje vertical de la placa.
 *
 * Cuatro puntos: vértice arriba, los dos extremos inferiores y la muesca
 * central que la vuelve una flecha de carta náutica y no un triángulo.
 */
const FLECHA = `M ${X * 3} ${X * 1.2}
   L ${X * 5} ${X * 4.25}
   L ${X * 3} ${X * 3.3}
   L ${X * 1} ${X * 4.25} Z`

export type VarianteLogo = 'horizontal' | 'vertical' | 'simbolo'
export type SobreFondo = 'claro' | 'oscuro'

/**
 * La placa sola: versión C del manual.
 *
 * Sobre fondo claro va grafito con la flecha en cal; sobre fondo oscuro se
 * invierte a placa cal con flecha grafito, que es como conserva el contraste
 * sin recolorear nada fuera de la paleta. La base de cobre es la misma en las
 * dos: es el acento y no cambia con el fondo.
 */
export function Placa({
  tamaño = 28,
  sobre = 'claro',
  trazo = false,
  className = '',
}: {
  tamaño?: number
  sobre?: SobreFondo
  /** Versión D: una sola tinta, para troquel, grabado o impresión monocroma. */
  trazo?: boolean
  className?: string
}) {
  const oscuro = sobre === 'oscuro'
  const fondoPlaca = trazo ? 'none' : oscuro ? 'var(--color-cal)' : 'var(--color-grafito)'
  const tintaFlecha = trazo
    ? 'currentColor'
    : oscuro
      ? 'var(--color-grafito)'
      : 'var(--color-cal)'
  const conBase = tamaño >= MINIMO_CON_BASE && !trazo

  return (
    <svg
      viewBox={`0 0 ${LADO} ${LADO}`}
      width={tamaño}
      height={tamaño}
      className={`shrink-0 ${className}`}
      role="img"
      aria-label="Compranorte"
    >
      <rect
        x="0"
        y="0"
        width={LADO}
        height={LADO}
        fill={fondoPlaca}
        {...(trazo ? { stroke: 'currentColor', strokeWidth: 2 } : {})}
      />
      <path d={FLECHA} fill={tintaFlecha} />
      {/* Base de cobre: 4x × 0,5x, apoyada a 1x del borde inferior. */}
      {conBase && (
        <rect
          x={X}
          y={X * 4.5}
          width={X * 4}
          height={X * 0.5}
          fill="var(--color-cobre-500)"
        />
      )}
    </svg>
  )
}

/**
 * El nombre y su bajada.
 *
 * «COMPRANORTE» va en una sola palabra y sin quiebre —la promesa es exactamente
 * esa, un canal único— en Archivo 800 con el +2 % de tracking que pide el
 * manual. La bajada va en Plex Mono, que el manual reserva para la bajada del
 * logo, los SKU, los precios y los números de cotización y OC.
 */
function Nombre({
  sobre,
  tamañoNombre,
  conBajada,
  centrado = false,
}: {
  sobre: SobreFondo
  tamañoNombre: number
  conBajada: boolean
  centrado?: boolean
}) {
  const oscuro = sobre === 'oscuro'
  return (
    <span className={`leading-none ${centrado ? 'text-center' : ''}`}>
      <span
        className="block font-sans"
        style={{
          fontWeight: 800,
          letterSpacing: '0.02em',
          fontSize: `${tamañoNombre}px`,
          color: oscuro ? 'var(--color-cal)' : 'var(--color-grafito)',
        }}
      >
        COMPRANORTE
      </span>
      {conBajada && (
        <span
          className="block font-mono"
          style={{
            fontWeight: 400,
            letterSpacing: '0.22em',
            fontSize: `${Math.max(8, Math.round(tamañoNombre * 0.42))}px`,
            marginTop: `${Math.round(tamañoNombre * 0.28)}px`,
            color: 'var(--color-acero-marca)',
          }}
        >
          ABASTECIMIENTO MINERO
        </span>
      )}
    </span>
  )
}

/**
 * La marca completa.
 *
 * `variante` sigue la regla del manual: si el espacio es más alto que ancho, la
 * vertical; si es una franja, la horizontal; en cuadrado pequeño, solo símbolo.
 * La barra de navegación y los membretes son franjas, así que su versión es la
 * horizontal — y es la que el manual muestra explícitamente para web y app.
 */
export function Logotipo({
  sobre = 'claro',
  variante = 'horizontal',
  compacto = false,
  tamaño,
}: {
  sobre?: SobreFondo
  variante?: VarianteLogo
  /** Sin la bajada, para barras muy apretadas. */
  compacto?: boolean
  /** Lado de la placa en px. Por defecto, el mínimo cómodo de cada versión. */
  tamaño?: number
}) {
  if (variante === 'simbolo') {
    return <Placa tamaño={tamaño ?? 28} sobre={sobre} />
  }

  if (variante === 'vertical') {
    const lado = tamaño ?? 64
    return (
      <span className="inline-flex flex-col items-center gap-3">
        <Placa tamaño={lado} sobre={sobre} />
        <Nombre
          sobre={sobre}
          tamañoNombre={Math.round(lado * 0.34)}
          conBajada={!compacto}
          centrado
        />
      </span>
    )
  }

  // B · horizontal. El gap es el área de resguardo: 1x del lado de la placa.
  const lado = tamaño ?? 34
  return (
    <span className="inline-flex items-center shrink-0" style={{ gap: `${lado / 6}px` }}>
      <Placa tamaño={lado} sobre={sobre} />
      <Nombre sobre={sobre} tamañoNombre={Math.round(lado * 0.46)} conBajada={!compacto} />
    </span>
  )
}
